import {
  GET_LIST,
  GET_ONE,
  UPDATE,
  GET_MANY_REFERENCE,
  GET_MANY,
  CREATE,
  DELETE,
  DELETE_MANY,
  UPDATE_MANY
} from 'react-admin';
import iccApis from './IccApis';
import { getIcError } from './utils';
import axios from 'axios';
import { sendPingToIcx } from '../utils/IcxPortal';
import { stringify } from '../components/forked/stringify';
import PassthroughApi from '../apis/passthrough/PassthroughApi';

const API_URL = 'v1';

const convertDataProviderRequestToHTTP = (api, type, resource, params) => {
  // Replace Date() objects with JSON ISO Dates on filters
  const filters = params.filter && JSON.parse(JSON.stringify(params.filter));

  switch (type) {
    case GET_LIST:
      if (api.paginated && params.pagination) {
        // With pagination, sort and filters
        const { page, perPage } = params.pagination;

        let additionalFilters = {};
        if (api.additionalGetManyFilterParam) {
          additionalFilters = { ...api.additionalGetOneFilterParam };
        }

        const combinedFilters = {
          ...filters,
          ...additionalFilters
        };

        let query = {};

        // Pagination
        if (perPage > 0) {
          query = Object.assign(
            {
              pageNumber: page - 1,
              pageSize: perPage
            },
            query
          );
        }

        // Sorting
        if (params.sort !== undefined) {
          const { field, order } = params.sort;
          if (field !== undefined) {
            query = Object.assign(
              {
                orderBy: field,
                orderDirection: order
              },
              query
            );
          }
        }

        return {
          url: `${API_URL}/${api.plural}?${stringify(
            query
          )}&${stringify(combinedFilters, { arrayFormat: 'open-ic' })}`,
          options: { method: 'GET' }
        };
      } else {
        var filter =
          filters && Object.keys(filters).length > 0
            ? `?${stringify(filters)}`
            : '';
        // Simple list of refernces (e.g. fundclasses)
        return {
          url: `${API_URL}/${api.plural}${filter}`,
          options: { method: 'GET' }
        };
      }
    case GET_ONE: {
      // Get a single resource
      const idQuery = getQueryIdsFromId(api, params);

      let additionalFilters;
      if (api.additionalGetOneFilterParam) {
        additionalFilters = { ...api.additionalGetOneFilterParam };
      }

      const urlFilters = additionalFilters
        ? `&${stringify(additionalFilters)}`
        : '';

      return {
        url: `${API_URL}/${api.singular}?${stringify(idQuery)}${urlFilters}`
      };
    }
    case GET_MANY:
      // Get many resources (use plural verb)
      const ids = encodeURI(`[${params.ids}]`);
      return { url: `${API_URL}/${api.plural}?id=${ids}` };
    case GET_MANY_REFERENCE: {
      // Get many as reference
      let query = {
        [params.target]: params.id
      };

      if (params.sort !== undefined) {
        const { field, order } = params.sort;
        if (field !== undefined) {
          query = Object.assign(
            {
              orderBy: field,
              orderDirection: order
            },
            query
          );
        }
      }
      return {
        url: `${API_URL}/${api.plural}?${stringify(query)}&${stringify(
          filters
        )}`,
        options: {}
      };
    }
    case UPDATE: {
      const idQuery = getQueryIdsFromData(api, params);
      // Update resource (can be POST or PUT, depends on the API)
      return {
        url: `${API_URL}/${api.singular}?${stringify(idQuery)}`,
        options: { method: api.updateMethod, data: JSON.stringify(params.data) }
      };
    }
    case UPDATE_MANY: {
      const idQuery = getQueryIdsFromId(api, params);
      return {
        url: `${API_URL}/${api.plural}?${stringify(idQuery)}`,
        options: { method: api.updateMethod, data: JSON.stringify(params.data) }
      };
    }
    case CREATE: {
      return {
        url: buildCreateUrl(api, params),
        options: { method: api.createMethod, data: JSON.stringify(params.data) }
      };
    }
    case DELETE: {
      const idQuery = getQueryIdsFromId(api, params);
      // Delete resource by ID
      return {
        url: `${API_URL}/${api.singular}?${stringify(idQuery)}`,
        options: { method: 'DELETE' }
      };
    }
    case DELETE_MANY:
      // Delete a list of resources
      const { idsQueryDataList, idsQuery } = getQueryIdsListFromIds(
        api,
        params
      );
      return {
        url: `${API_URL}/${api.plural}?${stringify(idsQuery)}`,
        options: { method: 'DELETE', data: JSON.stringify(idsQueryDataList) }
      };
    default:
      // Not supported yet
      throw new Error(`Unsupported action type ${type}`);
  }
};

const convertHTTPResponseToDataProvider = (
  api,
  response,
  type,
  resource,
  params
) => {
  switch (type) {
    case GET_LIST:
    case GET_MANY:
      if (api.paginated) {
        const { pageData, totalCount } = response;
        return {
          data: pageData.map(item => ({ ...item, id: buildId(api, item) })),
          total: totalCount
        };
      } else {
        return {
          data: response.map(item => ({ ...item, id: buildId(api, item) })),
          total: response.length
        };
      }
    case GET_MANY_REFERENCE:
      if (api.paginated) {
        const { pageData, totalCount } = response;
        return {
          data: pageData.map(item => ({
            ...item,
            id: buildId(api, item)
          })),
          total: totalCount
        };
      } else {
        return {
          data: response.map(item => ({ ...item, id: buildId(api, item) })),
          total: response.length
        };
      }
    case DELETE_MANY:
      return {
        data: params.ids
      };
    case DELETE:
      return {
        data: params.previousData
      };
    case UPDATE_MANY:
      return { data: [...response] };
    default:
      return { data: { ...response, id: buildId(api, response) } };
  }
};

const executeRequest = (
  api,
  url,
  options,
  type,
  resource,
  params,
  authentToken,
  tenant
) => {
  var request = {
    url: url,
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'IC-USER-CLIENT': Buffer.from(tenant).toString('base64'),
      'X-IC-ACCESS-TOKEN': authentToken
    }
  };

  let error = undefined;
  return axios
    .request(request)
    .then(r => {
      if (r.status !== 200 && r.status !== 201) {
        error = r.statusText;
      }
      let response = undefined;
      if (options && options.method === 'DELETE') {
        response = {};
      } else {
        if (r.headers['content-type'].indexOf('application/json') !== -1) {
          response = r.data;
        }
      }

      // when we catch a code status > 201 and < 300
      if (error !== undefined) {
        error = getIcError(response) || error;
        throw new Error(error);
      }

      return convertHTTPResponseToDataProvider(
        api,
        response,
        type,
        resource,
        params
      );
    })
    .catch(err => {
      error = getIcError(err.response);
      throw new Error(error);
    });
};

/**
 * Get IDs query params for POST and PUT (make query from params.data)
 */
function getQueryIdsFromData(api, params) {
  if (Array.isArray(api.id)) {
    const queryParams = {};
    api.id.forEach(id => {
      queryParams[id] = params.data[id];
    });
    return queryParams;
  } else {
    return { id: params.data.id };
  }
}

/**
 * Get IDs query params for GET_ONE (split params.id)
 */
function getQueryIdsFromId(api, params) {
  const queryParams = {};

  if (Array.isArray(api.id)) {
    const ids = params.id.split(':');
    if (ids.length !== api.id.length) {
      throw Error('Invalid composite key');
    }
    api.id.forEach((id, idx) => {
      queryParams[id] = ids[idx];
    });
  } else {
    const idKey = api.queryId || 'id';
    queryParams[idKey] = params.id;
  }
  return queryParams;
}

/**
 * Get IDs query params List for DELETE_MANY (split params.ids)
 */
function getQueryIdsListFromIds(api, params) {
  const idsQuery = {};
  const idsQueryDataList = [];

  if (Array.isArray(api.id)) {
    params.ids.forEach(id => {
      const queryParams = {};
      const ids = id.split(':');
      if (ids.length !== api.id.length) {
        throw Error('Invalid composite key');
      }
      api.id.forEach((id, idx) => {
        queryParams[id] = ids[idx];
      });
      idsQueryDataList.push(queryParams);
    });
    return { idsQueryDataList };
  } else {
    idsQuery['id'] = `[${params.ids}]`;
    return { idsQuery };
  }
}

function buildCreateUrl(api, params) {
  const queryParams = {
    ...getQueryIdsFromData(api, params),
    ...(api.singular === 'fund' && params.data?.fundBillingAcknowledgement
      ? { fundBillingAcknowledgement: true }
      : {})
  };

  return api.generatedId
    ? `${API_URL}/${api.singular}`
    : `${API_URL}/${api.singular}?${stringify(queryParams)}`;
}

/**
 * Build key for an item (can be composite)
 * @param {*} api
 * @param {*} item
 */
function buildId(api, item) {
  if (Array.isArray(api.id)) {
    const ids = [];
    api.id.forEach(id => {
      ids.push(item[id]);
    });
    return ids.join(':');
  } else {
    return item[api.id];
  }
}

/**
 * GET_MANY special handler, doing several calls as chunks
 */
function getMany(api, resource, params, token, tenant) {
  if (api.getMany === false) {
    // Special case : GET_MANY not supported by API, log to console and return an empty list
    console.warn(
      'Warning: getMany for this resource: %s is disabled, please take a look in IccApis.js ',
      resource
    );
    return Promise.resolve({ data: [] });
  }
  return getManyByChunks(
    params.ids,
    api.pluralId || 'id',
    api,
    undefined,
    token,
    tenant
  );
}

/**
 * GET_MANY_REFERENCE special handler, doing several calls as chunks
 * IMPORTANT: note that this function not hanling the sorting, it's called
 * only for some cases (chunk === true in IccApi and when chunk is needed)
 */
function getManyReference(api, params, token, tenant) {
  const filters = params.filter && JSON.parse(JSON.stringify(params.filter));
  return getManyByChunks(
    params.id,
    encodeURI(params.target),
    api,
    filters,
    token,
    tenant
  );
}

// We try to read this value from localStorage so that we can test the real max Request Size on different environments
const GET_MAX_PARAMS_SIZE = 3000; // 3000 characters max in a GET request to limit the URI size

/**
 * GET_MANY and GET_MANY_REFERENCE by chunks
 */
function getManyByChunks(allIds, targetId, api, filters, token, tenant) {
  const baseUrl = `${API_URL}/${api.plural}?${targetId}=`;

  //ReferenceArrayInput on RecipientsStep forces this unnecessary API call by its design (by react-admin).
  //This is the safest place to not allow that API call to be executed.
  //For that puropse there is new Resource created in App.js called 'fundsDistributionList', and also new API in IccApis.js
  if (api.fundsDistributionList === true) {
    return Promise.reject();
  }

  if (api.additionalGetManyFilterParam) {
    filters = { ...filters, ...api.additionalGetManyFilterParam };
  }

  const urlFilters = filters ? `&${stringify(filters)}` : '';

  const idsMaxLength =
    GET_MAX_PARAMS_SIZE -
    (50 + baseUrl.length + urlFilters.length + encodeURI('[]').length); // 50 protocol and host buffer

  let reqIds = encodeURI(`${allIds}`);
  const chunks = [];
  while (reqIds.length > idsMaxLength) {
    const lastIndex = reqIds.substring(0, idsMaxLength).lastIndexOf(',');
    chunks.push(reqIds.substring(0, lastIndex));
    reqIds = reqIds.substring(lastIndex + 1);
  }
  chunks.push(reqIds);

  // Create chunked API calls --> list of promises
  const promises = chunks.map(ids => {
    const request = {
      url: baseUrl + encodeURI('[') + ids + encodeURI(']') + urlFilters,
      headers: {
        'Content-Type': 'application/json',
        'IC-USER-CLIENT': Buffer.from(tenant).toString('base64'),
        'X-IC-ACCESS-TOKEN': token
      }
    };

    let error = undefined;
    return axios
      .request(request)
      .then(r => {
        // Process individual API response and return chunked data
        if (r.status !== 200 && r.status !== 201) {
          error = r.statusText;
        }
        let response = undefined;
        if (r.headers['content-type'].indexOf('application/json') !== -1) {
          response = r.data;
        }

        // when we catch a code status > 201 and < 300
        if (error !== undefined) {
          error = getIcError(response) || error;
          throw new Error(error);
        }
        return response;
      })
      .catch(err => {
        error = getIcError(err.response) || err.message;
        throw new Error(error);
      });
  });

  // Wait for all API calls to be completed and merge the responses into one list
  // Then return this as a promise for calling function
  return Promise.all(promises).then(responses => {
    var data;
    if (api.paginated) {
      data = [].concat.apply(
        [],
        responses.map(response => response.pageData)
      );
    } else {
      data = [].concat.apply([], responses);
    }

    return {
      data: data.map(item => ({ ...item, id: buildId(api, item) })),
      total: data.length
    };
  });
}

function getManyByChunksFundsForDistributionList(
  api,
  resource,
  params,
  token,
  tenant
) {
  const baseUrl = `${API_URL}/funds?userIds=`;

  var ids = params.filter.userIds.slice(0, -1);
  var allIds = ids.substring(1).split(',');

  const chunks = [];
  while (allIds.length > 300) {
    var chunk = allIds.slice(0, 300);
    chunks.push(chunk);
    allIds.splice(0, 300);
  }
  chunks.push(allIds);

  // Create chunked API calls --> list of promises
  const promises = chunks.map(ids => {
    const request = {
      url: baseUrl + encodeURI('[') + encodeURIComponent(ids) + encodeURI(']'),
      headers: {
        'Content-Type': 'application/json',
        'IC-USER-CLIENT': Buffer.from(tenant).toString('base64'),
        'X-IC-ACCESS-TOKEN': token
      }
    };

    let error = undefined;
    return axios
      .request(request)
      .then(r => {
        // Process individual API response and return chunked data
        if (r.status !== 200 && r.status !== 201) {
          error = r.statusText;
        }
        let response = undefined;
        if (r.headers['content-type'].indexOf('application/json') !== -1) {
          response = r.data;
        }

        // when we catch a code status > 201 and < 300
        if (error !== undefined) {
          error = getIcError(response) || error;
          throw new Error(error);
        }
        return response;
      })
      .catch(err => {
        error = getIcError(err.response) || err.message;
        throw new Error(error);
      });
  });

  // Wait for all API calls to be completed and merge the responses into one list
  // Then return this as a promise for calling function
  return Promise.all(promises).then(responses => {
    var data;
    if (api.paginated) {
      data = [].concat.apply(
        [],
        responses.map(response => response.pageData)
      );
    } else {
      data = [].concat.apply([], responses);
    }

    let uniqueFundIds = [];
    let uniqueData = [];
    data.forEach(d => {
      if (!uniqueFundIds.includes(d.name)) {
        uniqueFundIds.push(d.name);
        uniqueData.push(d);
      }
    });

    return {
      data: uniqueData.map(item => ({ ...item, id: buildId(api, item) })),
      total: uniqueData.length
    };
  });
}

function sortDataByField(data, field, order = 'ASC') {
  return [...data].sort((a, b) => {
    const aValue = a[field] ?? '';
    const bValue = b[field] ?? '';

    const aNormalized =
      typeof aValue === 'string' ? aValue.toLowerCase() : aValue;
    const bNormalized =
      typeof bValue === 'string' ? bValue.toLowerCase() : bValue;

    if (aNormalized < bNormalized) return order === 'ASC' ? -1 : 1;
    if (aNormalized > bNormalized) return order === 'ASC' ? 1 : -1;
    return 0;
  });
}

/**
 * Data provider
 */
export default (type, resource, params) => {
  const api = iccApis[resource];
  if (api === undefined) {
    throw new Error(`Unknown ICC API ${resource}`);
  }

  if (api.plural === 'passthrough') {
    const passthroughApi = new PassthroughApi();
    const result = passthroughApi.getFundMappingForFund(params.filter);
    return Promise.resolve(result);
  }
  if (api.plural === 'passthroughConnect') {
    const passthroughApi = new PassthroughApi();
    return passthroughApi.getPassthroughFunds(params.filter).then(result => {
      const { field, order } = params.sort || {};
      const sortedData = field
        ? sortDataByField(result.data, field, order)
        : result.data;

      return Promise.resolve({
        data: sortedData,
        total: sortedData.length
      });
    });
  }

  // Send a ping request to ICX portal every minute if launched inside ICXP
  sendPingToIcx();

  const token = sessionStorage.getItem('token');
  const tenant = sessionStorage.getItem('tenant');

  // Doing GET_MANY with chunks to avoid "URI too long" errors
  if (type === GET_MANY) {
    return getMany(api, resource, params, token, tenant);
  }

  // Doing GET_MANY_REFERENCE with chunks to avoid "URI too long" errors
  // IMPORTANT: note that getManyReference function not hanling the sorting, it's called
  // only for some cases (chunk passed as true in the params options and when chunk is needed)
  if (
    type === GET_MANY_REFERENCE &&
    params.options &&
    params.options.chunk &&
    Array.isArray(params.id)
  ) {
    return getManyReference(api, params, token, tenant);
  }

  if (
    type === GET_LIST &&
    params.filter &&
    params.filter.chunk &&
    params.filter.getFundsForDistributionList
  ) {
    return getManyByChunksFundsForDistributionList(
      api,
      resource,
      params,
      token,
      tenant
    );
  }

  const { url, options } = convertDataProviderRequestToHTTP(
    api,
    type,
    resource,
    params
  );

  return executeRequest(
    api,
    url,
    options,
    type,
    resource,
    params,
    token,
    tenant
  );
};
